"use client";

import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/shadcn/popover";
import { RadioGroup, RadioGroupItem } from "@/components/shadcn/radio-group";
import { useDebounce } from "@/hooks/useDebounce";
import { ChevronDown, Search, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useFilterParams } from "./filter-utils";
import FiltersDrawer, { CategoryOption } from "./filters-drawer";

const SORT_DEFAULT = "default";

// Labels come from the `seriesFilters` catalog: value -> message key.
const SORT_OPTIONS = [
    { value: SORT_DEFAULT, key: "sortDefault" },
    { value: "newest", key: "sortNewest" },
    { value: "oldest", key: "sortOldest" },
    { value: "highestRated", key: "sortHighestRated" },
    { value: "mostRated", key: "sortMostRated" },
] as const;

export default function SeriesFilters({
    categories,
}: {
    categories: CategoryOption[];
}) {
    const t = useTranslations("seriesFilters");
    const { searchParams, setParams } = useFilterParams();

    const [search, setSearch] = useState(searchParams.get("search") ?? "");
    const debouncedSearch = useDebounce(search, 300);

    const sort = searchParams.get("sort") ?? SORT_DEFAULT;
    const selectedSortKey =
        sort !== SORT_DEFAULT
            ? SORT_OPTIONS.find((option) => option.value === sort)?.key
            : undefined;

    useEffect(() => {
        setParams({ search: debouncedSearch });
    }, [debouncedSearch, setParams]);

    const handleSortChange = (value: string) => {
        setParams({ sort: value === SORT_DEFAULT ? null : value });
    };

    const hasActiveFilters =
        search.length > 0 ||
        sort !== SORT_DEFAULT ||
        Boolean(searchParams.get("titles")) ||
        Boolean(searchParams.get("categories")) ||
        Boolean(searchParams.get("rating"));

    const handleClearAll = () => {
        setSearch("");
        setParams({
            search: null,
            sort: null,
            titles: null,
            categories: null,
            rating: null,
        });
    };

    return (
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            {hasActiveFilters && (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClearAll}
                    aria-label={t("clearFilters")}
                >
                    <X className="size-4" />
                </Button>
            )}
            <div className="relative">
                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                    type="search"
                    placeholder={t("searchPlaceholder")}
                    className="w-full pl-9 sm:w-64"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <Popover>
                <PopoverTrigger asChild>
                    <Button variant="outline" className="w-48 justify-between">
                        <span className="truncate">
                            {selectedSortKey
                                ? t(selectedSortKey)
                                : t("sortPlaceholder")}
                        </span>
                        <ChevronDown className="size-4 shrink-0" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-2">
                    <RadioGroup
                        className="gap-0"
                        value={sort}
                        onValueChange={handleSortChange}
                    >
                        {SORT_OPTIONS.map((option) => (
                            <Label
                                key={option.value}
                                htmlFor={`sort-${option.value}`}
                                className="hover:bg-accent flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-normal"
                            >
                                <RadioGroupItem
                                    id={`sort-${option.value}`}
                                    value={option.value}
                                />
                                {t(option.key)}
                            </Label>
                        ))}
                    </RadioGroup>
                </PopoverContent>
            </Popover>
            <FiltersDrawer availableCategories={categories} />
        </div>
    );
}
